import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RightDefinition } from '../models/right-definition';
import { OrganizationsService } from './organizations.service';
import { ServiceBase } from './service-base';

@Injectable({
    providedIn: 'root',
})
export class RightDefinitonsService extends ServiceBase {
    constructor(
        private http: HttpClient,
        private organizationsServices: OrganizationsService
    ) {
        super();
    }

    getRightDefinitions(): Observable<RightDefinition[]> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return forkJoin([this.http.get<RightDefinition[]>(`${this.baseUrl}/api/definitions/rights`, httpOptions), this.organizationsServices.getOrganisations()]).pipe(
            map((result) => {
                const definitions = result[0];
                const organizations = result[1];

                definitions.forEach((definition) => {
                    const organization = organizations.find((x) => x.id === definition.organizationId);
                    definition.displayOrganization = organization ? organization.display : '';
                    definition.legacy =
                        this.organizationsServices.getCurrentOrganization() && definition.organizationId === this.organizationsServices.getCurrentOrganization().id ? false : true;
                });

                return definitions;
            })
        );
    }

    getRightDefinition(rightDefinitionName: string): Observable<RightDefinition> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.get<RightDefinition>(`${this.baseUrl}/api/definitions/rights/${encodeURIComponent(rightDefinitionName)}`, httpOptions).pipe(
            map((definition) => {
                definition.legacy =
                    this.organizationsServices.getCurrentOrganization() && definition.organizationId === this.organizationsServices.getCurrentOrganization().id ? false : true;

                return definition;
            })
        );
    }

    getRightDefinitionWithLegacy(rightDefinitionName: string): Observable<RightDefinition> {
        return this.getRightDefinition(rightDefinitionName).pipe(
            map((rightDefinition) => {
                rightDefinition.legacy =
                    this.organizationsServices.getCurrentOrganization() && rightDefinition.organizationId === this.organizationsServices.getCurrentOrganization().id ? false : true;

                return rightDefinition;
            })
        );
    }

    addRightDefinition(rightDefinition: RightDefinition): Observable<RightDefinition> {
        const httpOptions = {
            headers: new HttpHeaders().set('Content-Type', 'application/json').set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.post<RightDefinition>(`${this.baseUrl}/api/definitions/rights`, rightDefinition, httpOptions);
    }

    deleteRightDefinition(rightDefinitionName: string) {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.delete(`${this.baseUrl}/api/definitions/rights/${encodeURIComponent(rightDefinitionName)}`, httpOptions);
    }

    updateRightDefinition(rightDefinition: RightDefinition, globalNotification = true): Observable<RightDefinition> {
        const httpOptions = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/json')
                .set('global-notification', String(globalNotification))
                .set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.patch<RightDefinition>(`${this.baseUrl}/api/definitions/rights/${encodeURIComponent(rightDefinition.name)}`, rightDefinition, httpOptions);
    }
}
